"use client"

import type { CSSProperties, ReactNode } from "react"

// ────────────────────────────────────────────────────────────────────────────
// Viewport-separated marketing display.
//
// The public marketing pages must keep mobile and desktop as SEPARATE displays:
// mobile affordances (a sticky bottom CTA bar) must not appear on desktop, and a
// desktop-only block must not appear on mobile. We do this with a CSS media query at
// the 768px break (the same 768px `useIsDesktop()` uses, and the same technique the
// shared `MarketingHeader` already uses internally to collapse its nav).
//
// CSS, not a post-mount JS switch, on purpose: these pages are public and SEO-indexed,
// and the landing is a first-impression page. Both trees ship in the server HTML and CSS
// reveals exactly one, so there is no empty SSR, no hydration swap, and no mobile->desktop
// flash on load. Mount <MarketingResponsiveStyle/> once per page, then wrap viewport-
// specific blocks in <MobileOnly>/<DesktopOnly> (or use the class names directly).
// ────────────────────────────────────────────────────────────────────────────

export const MKT_MOBILE_ONLY = "mkt-mobile-only"
export const MKT_DESKTOP_ONLY = "mkt-desktop-only"

// !important so a wrapper carrying its own inline `display` (e.g. a flex row) can't defeat the
// hide/show. Put layout styles on an INNER element, not on MobileOnly/DesktopOnly itself.
const STYLE = `
.${MKT_DESKTOP_ONLY}{display:none!important}
@media (min-width:768px){
  .${MKT_MOBILE_ONLY}{display:none!important}
  .${MKT_DESKTOP_ONLY}{display:revert!important}
}
`

export function MarketingResponsiveStyle() {
  return <style dangerouslySetInnerHTML={{ __html: STYLE }} />
}

/** Renders only below 768px. The wrapper toggles display; children keep their own styles. */
export function MobileOnly({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className={MKT_MOBILE_ONLY} style={style}>{children}</div>
}

/** Renders only at/above 768px. */
export function DesktopOnly({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className={MKT_DESKTOP_ONLY} style={style}>{children}</div>
}
